import { inject } from '@angular/core'
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router'

import { Store } from '@ngrx/store'

import { Observable, map, of, tap } from 'rxjs'

import { PermissionValue } from '@flowx/designer/modules/workspaces/shared/roles'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'

import { whenLoaded } from './when-loaded'

import { ToastMessageService } from '../services/toast-message.service'
import { selectQueryParams } from '../store/selectors/router.selectors'

/**
 * The selected workspace's permissions, once they have finished loading. A load that failed leaves
 * none, so it counts as not permitted too: the page would break without them.
 */
const loadedWorkspacePermissions = (): Observable<Record<PermissionValue, boolean> | null> => {
  const { workspacePermissions } = inject(WorkspacesAndPermissionsStore)

  return whenLoaded(workspacePermissions.isLoading).pipe(
    map(() => (workspacePermissions.hasValue() ? workspacePermissions.value() : null))
  )
}

export function DefaultPermissionsGuard(
  permission: PermissionValue
): () => Observable<boolean | UrlTree> {
  return (): Observable<boolean | UrlTree> => {
    const router = inject(Router)

    return loadedWorkspacePermissions().pipe(
      map((permissions) => (permissions?.[permission] ? true : router.parseUrl('/403')))
    )
  }
}

export function EditProcessInstanceVariablesGuard(): CanActivateFn {
  return (_, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
    const store = inject(Store)
    const router = inject(Router)
    const { showEditVariables } = store.selectSignal(selectQueryParams)()
    if (!showEditVariables) {
      return of(true)
    }
    const toastMessageService = inject(ToastMessageService)
    return loadedWorkspacePermissions().pipe(
      map((permissions) => !!permissions?.['wks_process_instance_variables_edit']),
      tap((isPermitted) => {
        if (!isPermitted) {
          toastMessageService.showWarningMessage(`You don't have access to edit process variables.`)
        }
      }),
      map((isPermitted) => {
        if (isPermitted) {
          return true
        }
        const tree = router.parseUrl(state.url)
        delete tree.queryParams['showEditVariables']
        return tree
      })
    )
  }
}
